// Analytics, behind the visitor's choice. Nothing is loaded, set or sent until
// they allow it; every call below does nothing otherwise (and on local work,
// draft previews, or a browser that blocks the script).
const KEY = "nomus-consent";
export const CONSENT_EVENT = "nomus-consent";
export const CONSENT_OPEN = "nomus-consent-open";

const running = () =>
  typeof window !== "undefined" &&
  window.posthog &&
  typeof window.posthog.capture === "function";

// "yes", "no", or null when the visitor has not chosen yet.
export function consent() {
  try {
    const value = window.localStorage.getItem(KEY);
    return value === "yes" || value === "no" ? value : null;
  } catch {
    return null;
  }
}

// Remove what analytics left in the browser, so a "no" leaves nothing behind.
function forget(all) {
  try {
    Object.keys(window.localStorage)
      .filter((k) => k.startsWith("ph_") || (all && k.startsWith("__ph_")))
      .forEach((k) => window.localStorage.removeItem(k));
    const host = window.location.hostname;
    const domains = [
      "",
      `; domain=${host}`,
      `; domain=.${host.replace(/^www\./, "")}`,
    ];
    document.cookie
      .split(";")
      .map((c) => c.split("=")[0].trim())
      .filter((name) => name.startsWith("ph_"))
      .forEach((name) =>
        domains.forEach((d) => {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${d}`;
        }),
      );
  } catch {
    // Nothing to clear.
  }
}

export function setConsent(allowed) {
  try {
    window.localStorage.setItem(KEY, allowed ? "yes" : "no");
  } catch {
    // Without storage the choice lasts for this page only.
  }
  try {
    if (allowed) {
      if (running() && window.posthog.has_opted_out_capturing?.())
        window.posthog.opt_in_capturing();
      window.nomusAnalytics?.();
    } else {
      if (running()) {
        window.posthog.opt_out_capturing();
        window.posthog.stopSessionRecording?.();
      }
      forget();
    }
  } catch {
    // Analytics must never break the page.
  }
  window.dispatchEvent(new Event(CONSENT_EVENT));
}

// A client's private page is never measured or recorded, whatever they chose
// elsewhere on the site. Returns the function that ends the pause.
let paused = false;
export function pauseTracking() {
  paused = true;
  try {
    if (running()) {
      window.posthog.stopSessionRecording?.();
      window.posthog.opt_out_capturing();
    }
  } catch {
    // Analytics must never break the page.
  }
  return () => {
    paused = false;
    try {
      if (consent() === "yes" && running()) {
        window.posthog.opt_in_capturing();
        window.posthog.startSessionRecording?.();
      }
    } catch {
      // Analytics must never break the page.
    }
  };
}

export function track(event, properties) {
  try {
    if (!paused && consent() === "yes" && running())
      window.posthog.capture(event, properties);
  } catch {
    // Analytics must never break the page.
  }
}

// Called once an email is known. Never pass message contents.
export function identify(email, properties = {}) {
  try {
    if (!paused && consent() === "yes" && running() && email)
      window.posthog.identify(email, { email, ...properties });
  } catch {
    // Analytics must never break the page.
  }
}

// A "no" from an earlier page: analytics is not running now, so clear whatever
// that page wrote on its way out.
if (typeof window !== "undefined" && consent() === "no") forget(true);
